import { Form, Formik } from 'formik';
import Button from 'shared/components/Button';
import FormField from 'shared/components/form/FormField';
import FormikInput from 'shared/components/form/FormikInput';
import Modal from 'shared/components/modal/Modal';
import StyledAutocomplete from 'shared/components/StyledAutocomplete';
import InternationalPhones from 'shared/components/phone/InternationalPhones';
import { createPhoneValidationTest } from 'shared/components/phone/phoneValidation';
import * as Yup from 'yup';
import { ApplicantRoleModel } from '../../models/ApplicantRole';
import { EventReferentModel } from '../../models/Evento';
import { EventReferentPayload } from '../../services/useEventoService';
import { Pencil } from 'lucide-react';

interface FormValues {
  name: string;
  roleId: number | null;
  email: string;
  phone: string;
}

interface Props {
  open: boolean;
  roles: ApplicantRoleModel[];
  referent?: EventReferentModel | null;
  onlyView?: boolean;
  isSaving: boolean;
  onEdit?: () => void;
  onClose: () => void;
  onSave: (payload: EventReferentPayload) => void;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required('El nombre es requerido'),
  roleId: Yup.number().nullable().required('El rol es requerido'),
  email: Yup.string().email('Email inválido'),
  phone: Yup.string().test(
    'phone-validation',
    'Número de teléfono inválido',
    createPhoneValidationTest('AR', true)
  )
});

const FORM_ID = 'referent-form';

const ReferentFormModal = ({
  open,
  roles,
  referent,
  onlyView,
  onEdit,
  isSaving,
  onClose,
  onSave
}: Props) => {
  const initialValues: FormValues = {
    name: referent?.name ?? '',
    roleId: referent?.role?.id ?? null,
    email: referent?.email ?? '',
    phone: referent?.phone ?? ''
  };

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(values) =>
        !onlyView
          ? onSave({
              name: values.name,
              role_id: values.roleId as number,
              email: values.email || undefined,
              phone: values.phone || undefined
            })
          : onEdit?.()
      }
    >
      {({ values, errors, touched, setFieldValue, dirty }) => (
        <Modal
          open={open}
          onClose={onClose}
          title={referent ? 'Editar referente' : 'Nuevo referente'}
          headerAction={
            onlyView && (
              <button
                type="submit"
                form={FORM_ID}
                className="text-inkMuted transition hover:text-secondary-600 dark:text-inkMuted-dark"
              >
                <Pencil size={16} />
              </button>
            )
          }
          footer={
            !onlyView && (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={onClose}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  form={FORM_ID}
                  size="sm"
                  disabled={isSaving || !dirty}
                >
                  {isSaving ? 'Guardando...' : 'Guardar'}
                </Button>
              </>
            )
          }
        >
          <Form id={FORM_ID} className="flex flex-col gap-4">
            <FormikInput name="name" label="Nombre" disabled={onlyView} />
            <FormField label="Rol" error={touched.roleId && errors.roleId}>
              <StyledAutocomplete<ApplicantRoleModel>
                options={roles}
                disabled={onlyView}
                value={roles.find((role) => role.id === values.roleId) ?? null}
                getOptionLabel={(role) => role?.name ?? ''}
                onChange={(value) =>
                  setFieldValue('roleId', value ? value.id : null)
                }
                placeholder="Seleccionar..."
              />
            </FormField>
            <FormikInput
              name="email"
              label="Email (opcional)"
              disabled={onlyView}
            />
            <InternationalPhones
              name="phone"
              label="Teléfono (opcional)"
              disabled={onlyView}
            />
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

export default ReferentFormModal;
